import { expandProjection, monthCoverageKey, scheduleCoverage, sumProjection, } from './budgetEngine/buildProjection.js';
import { lineKeyId, } from './budgetEngine/projection.js';
import { dateString } from './dateString.js';
import { INCOME_COMMITMENT_TYPE, isCoveredByUnitBilling } from './incomeCommitments.js';
import { RENT_ROLL_SCHEDULE_TYPE, rentalRentOf } from './rentalRent.js';
export function incomeProjectionPrecedence(type) {
    switch (type) {
        case INCOME_COMMITMENT_TYPE.runRate:
            return 'run_rate';
        case INCOME_COMMITMENT_TYPE.recurring:
        case INCOME_COMMITMENT_TYPE.activity:
        case INCOME_COMMITMENT_TYPE.interest:
        case INCOME_COMMITMENT_TYPE.specialAssessment:
            return 'repeating_override';
        default:
            return 'additive';
    }
}
function incomeLineOf(occurrence) {
    return occurrence.ref.direction === 'income' && occurrence.key.kind === 'taxonomy'
        ? lineKeyId(occurrence.key)
        : undefined;
}
function forecastCoverage(input, expansion, model) {
    return scheduleCoverage(input, expansion, (ref) => ref.direction === 'income' &&
        incomeProjectionPrecedence(model.incomeTypes.get(ref.displayId) ?? null) ===
            'repeating_override');
}
function withRunRatePrecedence(input, expansion, model) {
    const covered = forecastCoverage(input, expansion, model);
    return expansion.occurrences.filter((occurrence) => {
        const line = incomeLineOf(occurrence);
        return !(line !== undefined &&
            incomeProjectionPrecedence(model.incomeTypes.get(occurrence.ref.displayId) ?? null) ===
                'run_rate' &&
            covered.has(monthCoverageKey(line, occurrence.index)));
    });
}
const RENT_MODEL_REF = {
    direction: 'income',
    displayId: 'rental-rent-model',
    name: 'Effective gross rent',
};
function withRentalRent(input, expansion, occurrences, model) {
    if (model.rental === undefined)
        return [...occurrences];
    const asOfMonth = input.asOfDate.slice(0, 7);
    const from = expansion.periodIndexOf(asOfMonth) ?? 0;
    const billed = occurrences.filter((occurrence) => !(occurrence.index >= from &&
        model.incomeTypes.get(occurrence.ref.displayId) === RENT_ROLL_SCHEDULE_TYPE &&
        occurrence.key.kind === 'taxonomy' &&
        isCoveredByUnitBilling('rental', occurrence.key.category)));
    const term = {
        kind: 'monthly',
        from: dateString(`${asOfMonth}-01`),
        until: undefined,
    };
    const rent = rentalRentOf(model.rental).lines.flatMap(({ taxonomy, monthlyAmount }) => {
        if (monthlyAmount === 0)
            return [];
        const classification = { kind: 'taxonomy', ...taxonomy };
        const key = expansion.lineOf('income', classification);
        const item = { amount: monthlyAmount, description: taxonomy.subcategory, classification };
        return expansion.periods.slice(from).map((period, offset) => ({
            ref: RENT_MODEL_REF,
            dueDate: dateString(`${period.year}-${String(period.month).padStart(2, '0')}-01`),
            term,
            item,
            key,
            index: from + offset,
        }));
    });
    return [...billed, ...rent];
}
function rentModelCoverage(input, expansion, model) {
    if (model.rental === undefined)
        return new Set();
    const from = expansion.periodIndexOf(input.asOfDate.slice(0, 7)) ?? 0;
    return new Set(rentalRentOf(model.rental).lines.flatMap(({ taxonomy }) => {
        const key = expansion.lineOf('income', { kind: 'taxonomy', ...taxonomy });
        if (key.kind !== 'taxonomy')
            return [];
        const line = lineKeyId(key);
        return expansion.periods
            .slice(from)
            .map((_, offset) => monthCoverageKey(line, from + offset));
    }));
}
export function buildIncomeProjection(input, model) {
    const expansion = expandProjection(input);
    return sumProjection(input, withRentalRent(input, expansion, withRunRatePrecedence(input, expansion, model), model), new Set([...expansion.valued, ...rentModelCoverage(input, expansion, model)]));
}
//# sourceMappingURL=incomeProjection.js.map